import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import axios from 'axios';
import BotonDetalles from './BotonDetalles';
import BotonHistorial from './BotonHistorial'
import SearchBar from '../SearchBar';
import { AiOutlineCheck, AiOutlineClose, AiOutlineMinus } from 'react-icons/ai';
import { RiFileExcel2Line } from 'react-icons/ri';
import { useAuth } from '../../../context/AuthContext';
import useUserRole from '../../../getRole';
import { exportToExcel } from './ExcelExporter';

const DriversTable = () => {
    const [drivers, setDrivers] = useState([]);
    const [showIcons, setShowIcons] = useState(window.innerWidth <= 1579);
    const [filteredDrivers, setFilteredDrivers] = useState(drivers);
    const { user } = useAuth();
    const userRole = useUserRole(user);

    const handleSearch = (searchTerm) => {
        if (!searchTerm.trim()) {
            setFilteredDrivers(drivers);
        } else {
            const filtered = drivers.filter((driver) => {
                const fullName = `${driver.name} ${driver.last_name}`.toLowerCase();
                const id = driver.driver_id.toLowerCase();
                const matricula = driver.driver_vehicle?.vehicle_plates.toLowerCase() || '';
                const fecha = new Date(driver.date_registration).toLocaleDateString().toLowerCase() || '';

                return fullName.includes(searchTerm.toLowerCase()) || id.includes(searchTerm.toLowerCase()) || matricula.includes(searchTerm.toLowerCase()) || fecha.includes(searchTerm.toLowerCase());
            });
            setFilteredDrivers(filtered);
        }
    };

    useEffect(() => {
        setFilteredDrivers(drivers);
    }, [drivers]);

    useEffect(() => {
        const fetchConductoresData = async () => {
            try {
                const response = await axios.get(
                    process.env.REACT_APP_API_DRIVERS,
                    {
                        headers: {
                            "x-hasura-admin-secret": process.env.REACT_APP_API_KEY,
                        },
                    }
                );
                setDrivers(response.data.drivers);
            } catch (error) {
                console.error("Error fetching driver data:", error);
            }
        };

        const handleResize = () => {
            setShowIcons(window.innerWidth <= 1579);
        };
        window.addEventListener('resize', handleResize);
        fetchConductoresData();
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const getDocumentsIcon = (documents) => {
        if (showIcons) {
            return documents ? <AiOutlineCheck className='text-2xl' /> : <AiOutlineClose className='text-2xl' />;
        } else {
            return documents ? "Si" : "No";
        }
    };

    const getStatusClass = (status) => {
        if (status === "Activo") {
            return "bg-greenE text-white";
        } else {
            return "bg-yellowE text-black";
        }
    };

    const columns = [
        {
            name: 'ID',
            selector: (row) => row.driver_id,
            sortable: true,
            cell: (row) => <strong className='font-custom'>{row.driver_id}</strong>,
        },
        {
            name: 'Nombre',
            selector: row => `${row.name} ${row.last_name}`,
            sortable: true,
            cell: (row) => <div className='font-custom text-md'>{row.name} {row.last_name}</div>,
        },
        {
            name: 'Matrícula',
            selector: row => row.driver_vehicle?.vehicle_plates || "N/A",
            sortable: true,
            cell: (row) => <div className='font-custom'>{row.driver_vehicle?.vehicle_plates || "N/A"}</div>,
        },
        {
            name: 'Inicio de actividad',
            selector: row => {
                const date = row.date_registration ? new Date(row.date_registration) : null;
                return date ? date.toLocaleDateString() : "N/A";
            },
            sortable: true,
        },
        {
            name: 'Estatus',
            selector: row => row.status || "Pendiente",
            sortable: true,
            cell: row => (
                <span className={`status-badge w-box py-1 font-custom text-md text-center rounded-full ${getStatusClass(row.status)}`}>
                    {showIcons ? (
                        <div className="flex items-center justify-center">
                            {row.status === "Activo" ? (
                                <AiOutlineCheck className='text-2xl' />
                            ) : (
                                row.status === "Pendiente" ? (
                                    <AiOutlineMinus className='text-2xl' />
                                ) : (
                                    <AiOutlineClose className='text-2xl' />
                                )
                            )}
                        </div>
                    ) : (
                        row.status || "Pendiente"
                    )}
                </span>
            ),
        },

        {
            name: 'Documentos',
            selector: row => row.docVerification,
            sortable: true,
            cell: row => (
                <span className={`documents-badge py-1 w-box text-center rounded-full text-md font-custom ${row.docVerification === true ? "bg-greenE text-white" : "bg-redD text-white"}`}>
                    <div className="flex items-center justify-center">
                        {getDocumentsIcon(row.docVerification)}
                    </div>
                </span>
            ),
        },
        {
            name: 'Opciones',
            selector: (row) => row.options,
            cell: (row) => (
                <div className="flex items-center">
                    <BotonDetalles rowData={row} />
                    <BotonHistorial rowData={row} />
                </div>
            ),
        },
    ];

    const paginationComponentOptions = {
        rowsPerPageText: "Filas por página",
        rangeSeparatorText: "de",
        selectAllRowsItem: true,
        selectAllRowsItemText: "Todos",
    };

    const columnConfig = [
        { label: 'ID', key: 'driver_id' },
        { label: 'Nombre', key: 'name' },
        { label: 'Matricula', key: 'driver_vehicle.vehicle_plates', transform: (value) => (value && value.vehicle_plates) ? value.vehicle_plates : '' },
        { label: 'Inicio de actividad', key: 'date_registration' },
        { label: 'Estatus', key: 'status' },
        { label: 'Documentos', key: 'docVerification' },
    ];

    const exportToExcelLocal = () => {
        exportToExcel(filteredDrivers, columnConfig);
    };

    return (
        <div className='mb-4'>
            <SearchBar onSearch={handleSearch} />
            {(userRole === 'Admin' || userRole === 'Master') && (
                <button
                    className='bg-green-600 p-2 text-white font-bold mb-2 flex items-center space-x-2 hover:bg-green-900'
                    onClick={exportToExcelLocal}
                >
                    <RiFileExcel2Line className='text-2xl mr-1' />
                    Descargar Excel
                </button>
            )}
            <DataTable
                columns={columns}
                data={filteredDrivers}
                highlightOnHover
                responsive={true}
                pagination
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
                paginationComponentOptions={paginationComponentOptions}
                customStyles={{
                    rows: {
                        style: {
                            borderTop: '1px solid #CBD5E0',
                        },
                    },
                    selected: {
                        style: {
                            color: "#008000",
                        },
                    },
                    headCells: {
                        style: {
                            fontSize: '1.25rem',
                            textAlign: 'center',
                        },
                    },
                    cells: {
                        style: {
                            marginTop: '1rem',
                            marginBottom: '1rem',
                            textAlign: 'center',
                            fontSize: '1rem',
                        },
                    },
                    pagination: {
                        style: {
                            fontSize: '1rem',
                            borderBottomLeftRadius: '2rem',
                            borderBottomRightRadius: '2rem',
                        },
                    },
                }}
            />
        </div>
    );
};

export default React.memo(DriversTable);